export class TaskDefnNode {
  readonly id: number;
  seq: number;
  readonly parentNode: TaskDefnNode | undefined;
  readonly children: TaskDefnNode[] | undefined;
  isExpanded: boolean;

  constructor(id: number, seq: number, parentNode: TaskDefnNode | undefined = undefined) {
    this.id = id;
    this.seq = seq;
    this.parentNode = parentNode;
    this.children = [];
    this.isExpanded = false;
    if (parentNode) {
      parentNode.children.push(this);
    }
  }

  recurse<T>(callback: (node: TaskDefnNode) => T): T {
    let result = callback(this);
    if (result) {
      return result;
    }
    for (const child of this.children) {
      result = child.recurse(callback);
      if (result) {
        return result;
      }
    }
    return undefined as unknown as T;
  }

  getNodeById(id: number): TaskDefnNode | undefined {
    return this.recurse((node) => {
      if (node.id === id) {
        return node;
      }
      return undefined;
    });
  }

  getChildrenInOrder(): TaskDefnNode[] {
    const temp: TaskDefnNode[] = [...this.children];
    temp.sort((a, b) => a.seq - b.seq);
    return temp;
  }

  getNeighbour(neighbour: 'next' | 'previous'): TaskDefnNode | undefined {
    if (!this.parentNode) {
      return undefined;
    }
    const siblings = this.parentNode.getChildrenInOrder();
    const index = siblings.indexOf(this);
    if (index === -1) {
      return undefined;
    }
    if (neighbour === 'next') {
      if (index === siblings.length - 1) {
        return undefined;
      }
      return siblings[index + 1];
    } else {
      if (index === 0) {
        return undefined;
      }
      return siblings[index - 1];
    }
  }

  getMaxChildSeq(): number {
    if (!this.children || this.children.length === 0) {
      return 0;
    }
    return Math.max(...this.children.map((child) => child.seq));
  }

  addIfNew(id: number, seq: number, parentNodeId: number): TaskDefnNode {
    const existingNode = this.getNodeById(id);
    if (existingNode) {
      return existingNode;
    }
    const parentNode = this.getNodeById(parentNodeId);
    if (!parentNode) {
      throw new Error(`[TaskDefnTree] Parent node with id ${parentNodeId} not found`);
    }
    console.log(`[TaskDefnTree] Added node with id ${id} and seq ${seq} under parent node with id ${parentNodeId}`);
    return new TaskDefnNode(id, seq, parentNode);
  }

  removeChildById(id: number): void {
    const index = this.children.findIndex((child) => child.id === id);
    if (index === -1) {
      throw new Error(`[TaskDefnTree] Child node with id ${id} not found`);
    }
    this.children.splice(index, 1);
    console.log(`[TaskDefnTree] Removed node with id ${id} from parent node with id ${this.id}`);
  }

  remove(): void {
    if (!this.parentNode) {
      throw new Error(`[TaskDefnTree] Cannot remove root node`);
    }
    this.parentNode.removeChildById(this.id);
  }
}
